'use client';

import { useEffect, useRef, useCallback } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/lib/auth-store';
import { assumeCookieSession, hasAuthTokenOrCookie, restoreMemoryAccessTokenFromCookie } from '@/lib/api';

/** Admin inactivity timeout: 12 hours. */
const ADMIN_INACTIVITY_TIMEOUT_MS = 12 * 60 * 60 * 1000;
/** All other users inactivity timeout: 60 days. */
const USER_INACTIVITY_TIMEOUT_MS = 60 * 24 * 60 * 60 * 1000;
/** Session refresh interval in milliseconds (5 minutes). */
const SESSION_REFRESH_MS = 5 * 60 * 1000;

function isParticipantQuizPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const path = pathname.split('?')[0];
  return path.startsWith('/participate') || path.startsWith('/quiz/');
}

function isPublicAnonymousPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const path = pathname.split('?')[0];
  return (
    path === '/' ||
    path.startsWith('/participate') ||
    path.startsWith('/activity-invite') ||
    path.startsWith('/quiz') ||
    path.startsWith('/f/') ||
    path === '/f' ||
    path.startsWith('/verify/form/') ||
    path.startsWith('/events') ||
    path.startsWith('/pricing') ||
    path.startsWith('/host-events') ||
    path.startsWith('/features') ||
    path.startsWith('/resources') ||
    path.startsWith('/articles') ||
    path.startsWith('/developers') ||
    path.startsWith('/privacy-policy') ||
    path.startsWith('/terms') ||
    path.startsWith('/org/') ||
    path.startsWith('/register') ||
    path.startsWith('/signup') ||
    path.startsWith('/become-organizer') ||
    path.startsWith('/login') ||
    path.startsWith('/forgot-password') ||
    path.startsWith('/reset-password') ||
    path.startsWith('/setup-password') ||
    path.startsWith('/verify-email') ||
    path.startsWith('/scanner-login') ||
    path.startsWith('/maintenance') ||
    path.startsWith('/guides') ||
    path.startsWith('/help')
  );
}

export function AuthInitializer() {
  const pathname = usePathname();
  const router = useRouter();
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const skipAuth = isPublicAnonymousPath(pathname);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const sha = process.env.NEXT_PUBLIC_COMMIT_SHA || process.env.NEXT_PUBLIC_BUILD_SHA || 'dev';
      console.log(`[EnthiosHub] Frontend Build SHA: ${sha}`);
    }
  }, []);

  useEffect(() => {
    let unmounted = false;
    let abortController: AbortController | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    void Promise.resolve(useAuthStore.persist.rehydrate()).finally(() => {
      if (unmounted) return;
      restoreMemoryAccessTokenFromCookie();
      useAuthStore.getState().setHasHydrated(true);

      const path = pathname?.split('?')[0] || '';
      const isRegisterRoute =
        path === '/register' ||
        path.startsWith('/register/') ||
        path === '/signup' ||
        path.startsWith('/signup/');

      // Do not run checkAuth or session verification on public registration routes
      if (isRegisterRoute) {
        useAuthStore.setState({ authChecking: false });
        return;
      }

      // Public quiz pages must not refresh an organizer session. A 401 from
      // /auth/refresh was clearing that session and the hosting fallback then
      // showed the public landing page.
      if (isParticipantQuizPath(path)) {
        useAuthStore.setState({ authChecking: false });
        return;
      }

      const persisted = useAuthStore.getState();
      const hasTokenOrCookie = hasAuthTokenOrCookie();

      // Skip the call on public pages if there is no auth token/cookie
      if (skipAuth && !hasTokenOrCookie) {
        // Clear any stale persisted user state from localStorage when no valid credentials exist
        if (persisted.isAuthenticated || persisted.user) {
          useAuthStore.getState().clearSessionLocal();
        }
        useAuthStore.setState({ authChecking: false });
        return;
      }

      if (hasTokenOrCookie) {
        assumeCookieSession();
      }

      const controller = new AbortController();
      abortController = controller;
      let hasCompleted = false;

      // 8-second initialization timeout: abort and treat user as logged out if stalled
      timeoutId = setTimeout(() => {
        if (!hasCompleted && !unmounted) {
          controller.abort();
          console.warn('[AuthInitializer] Session check timed out after 8s; treating user as logged out.');
          useAuthStore.getState().clearSessionLocal();
          useAuthStore.setState({ authChecking: false });
        }
      }, 8000);

      void checkAuth({ reason: 'app_init', signal: controller.signal })
        .then(() => {
          if (controller.signal.aborted) {
            useAuthStore.getState().clearSessionLocal();
          }
        })
        .catch((error) => {
          if (!unmounted) {
            console.warn('[AuthInitializer] Session check failed; treating user as logged out:', error);
            useAuthStore.getState().clearSessionLocal();
          }
        })
        .finally(() => {
          hasCompleted = true;
          if (timeoutId) clearTimeout(timeoutId);
          if (!unmounted) {
            useAuthStore.setState({ authChecking: false });
          }
        });
    });

    return () => {
      unmounted = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (abortController) abortController.abort();
    };
  }, [checkAuth, skipAuth, pathname]);

  // --- Inactivity auto-logout ---
  const handleInactivityLogout = useCallback(async () => {
    if (!useAuthStore.getState().isAuthenticated) return;
    await logout();
    // Full page reload to clear all in-memory state
    window.location.href = '/login?reason=session_expired';
  }, [logout]);

  const resetInactivityTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (useAuthStore.getState().isAuthenticated) {
      const currentUser = useAuthStore.getState().user;
      const isAdmin = currentUser?.role === 'admin';
      const timeoutMs = isAdmin ? ADMIN_INACTIVITY_TIMEOUT_MS : USER_INACTIVITY_TIMEOUT_MS;
      timerRef.current = setTimeout(handleInactivityLogout, timeoutMs);
    }
  }, [handleInactivityLogout]);

  useEffect(() => {
    if (isParticipantQuizPath(pathname) || !isAuthenticated) {
      // Not logged in — clear any pending timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Start the inactivity timer
    resetInactivityTimer();

    const activityEvents = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'] as const;
    const onActivity = () => resetInactivityTimer();

    for (const evt of activityEvents) {
      window.addEventListener(evt, onActivity, { passive: true });
    }

    // Refresh the backend session so it doesn't expire while the user is active/inactive
    const refreshInterval = setInterval(() => {
      if (useAuthStore.getState().isAuthenticated) {
        void useAuthStore.getState().checkAuth({ reason: 'session_refresh' });
      }
    }, SESSION_REFRESH_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      clearInterval(refreshInterval);
      for (const evt of activityEvents) {
        window.removeEventListener(evt, onActivity);
      }
    };
  }, [isAuthenticated, pathname, resetInactivityTimer]);

  useEffect(() => {
    const onSessionExpired = () => useAuthStore.getState().clearSessionLocal();
    const onAdminAccessDenied = () => useAuthStore.getState().clearSessionLocal();
    const onTwoFactorBlocked = (event: Event) => {
      const code = (event as CustomEvent<{ code?: string }>).detail?.code;
      if (code === 'TWO_FACTOR_SETUP_REQUIRED' || code === 'TWO_FACTOR_REQUIRED') {
        useAuthStore.getState().applyAdminTwoFactorBlock(code);
      }
    };
    const onApiForbidden = (event: Event) => {
      const msg = (event as CustomEvent).detail?.message || 'Permission denied';
      toast.error(msg, { id: 'api-forbidden' });
    };
    const onApiServerError = (event: Event) => {
      const msg = (event as CustomEvent).detail?.message || 'Server error';
      toast.error(msg, { id: 'api-error' });
    };

    window.addEventListener('auth:session-expired', onSessionExpired);
    window.addEventListener('auth:admin-access-denied', onAdminAccessDenied);
    window.addEventListener('admin:two-factor-blocked', onTwoFactorBlocked);
    window.addEventListener('api:forbidden', onApiForbidden);
    window.addEventListener('api:server-error', onApiServerError);
    return () => {
      window.removeEventListener('auth:session-expired', onSessionExpired);
      window.removeEventListener('auth:admin-access-denied', onAdminAccessDenied);
      window.removeEventListener('admin:two-factor-blocked', onTwoFactorBlocked);
      window.removeEventListener('api:forbidden', onApiForbidden);
      window.removeEventListener('api:server-error', onApiServerError);
    };
  }, []);

  return null;
}
